import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { UploadCloud, Loader2, Check, ArrowRight, Image as ImageIcon, AlertCircle, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/hooks/use-toast';
import {
  getEtsyDraftVectorStatus,
  getEtsyMassMatchProgress,
  matchExistingEtsyProductionVectors,
  startEtsyDraftVectorization,
  assignImportedItemFullSizeImage,
  type EtsyMassMatchProgress,
  type EtsyPendingOrder,
  uploadAndMatchEtsyProductionFiles,
} from '@/services/etsyService';
import { getSignedUrlForFile } from '@/services/uploadService';
import { getPreferredEtsyItemPreviewUrl } from '@/components/etsy/etsyPreview';

interface EtsyDraftSlot {
  id: string;
  title: string;
  thumbnailUrl?: string;
  originalPrintWidthIn?: number;
  originalPrintHeightIn?: number;
  expectedWidthIn?: number;
  expectedHeightIn?: number;
  status: 'pending' | 'vectorizing' | 'ready' | 'matched' | 'auto_matched';
  matchedFileId?: string;
  matchedFileUrl?: string;
  similarity?: number;
}

interface UploadingFile {
  id: string;
  file: File;
  previewUrl: string;
  status: 'queued' | 'uploading' | 'vectorizing' | 'matching' | 'ready' | 'matched' | 'failed';
  progress: number;
  error?: string;
  matchedItemId?: string;
  uploadedFileId?: string;
  similarity?: number | null;
}

const TARGET_IMAGE_PPI = 300;

const sleep = (delayMs: number): Promise<void> => new Promise((resolve) => {
  window.setTimeout(resolve, delayMs);
});

const formatPrintSizeIn = (value: number): string => (
  value >= 10 ? value.toFixed(1) : value.toFixed(2)
);

const formatPrintSizeLabel = (widthIn: number | undefined, heightIn: number | undefined): string | null => (
  widthIn && heightIn ? `${formatPrintSizeIn(widthIn)} x ${formatPrintSizeIn(heightIn)} in` : null
);

export const EtsyMassMatchTab = ({
  orders,
  onCompleted,
}: {
  orders: ReadonlyArray<EtsyPendingOrder>;
  onCompleted?: () => void;
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [draftSlots, setDraftSlots] = useState<EtsyDraftSlot[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [isVectorizingDrafts, setIsVectorizingDrafts] = useState(false);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [progress, setProgress] = useState<EtsyMassMatchProgress | null>(null);
  const [vectorizedDraftItemIds, setVectorizedDraftItemIds] = useState<ReadonlySet<string>>(() => new Set());
  const [hasLoadedVectorStatus, setHasLoadedVectorStatus] = useState(false);
  const previewUrlsRef = useRef<string[]>([]);
  const activeUploadIdsRef = useRef<ReadonlyArray<string>>([]);

  useEffect(() => {
    setDraftSlots((previous) => {
      const previousById = new Map(previous.map((slot) => [slot.id, slot]));
      
      // Only include items that don't have a full-size image assigned yet,
      // OR items that we are currently matching in this session.
      const unmatchedItems = orders.flatMap(order => order.items).filter(item => {
        // If it's already matched in the backend, we usually hide it from here
        // UNLESS we have it in our local state as something we just matched/are matching
        const previousSlot = previousById.get(item.id);
        const isLocallyActive = previousSlot?.status === 'matched' || previousSlot?.status === 'auto_matched';
        
        return !item.fullSizeUploadedFile || isLocallyActive;
      });

      const slots = unmatchedItems.map((item) => {
        const previousSlot = previousById.get(item.id);
        
        let status: EtsyDraftSlot['status'] = previousSlot?.status 
          ?? (vectorizedDraftItemIds.has(item.id) ? 'ready' : 'pending');

        return {
          id: item.id,
          title: item.title,
          thumbnailUrl: getPreferredEtsyItemPreviewUrl(item) ?? undefined,
          originalPrintWidthIn: item.imageOriginalWidthPx ? item.imageOriginalWidthPx / TARGET_IMAGE_PPI : undefined,
          originalPrintHeightIn: item.imageOriginalHeightPx ? item.imageOriginalHeightPx / TARGET_IMAGE_PPI : undefined,
          expectedWidthIn: item.matchedWidthIn ?? undefined,
          expectedHeightIn: item.matchedHeightIn ?? undefined,
          status,
          matchedFileId: previousSlot?.matchedFileId,
          matchedFileUrl: previousSlot?.matchedFileUrl,
          similarity: previousSlot?.similarity,
        };
      });

      // Sort slots: priority 0 for pending/ready/auto_matched, priority 1 for matched (confirmed)
      // Note: in this view 'matched' only means confirmed in THIS session
      return slots.sort((a, b) => {
        const getPriority = (s: EtsyDraftSlot['status']) => (s === 'matched' ? 1 : 0);
        return getPriority(a.status) - getPriority(b.status);
      });
    });
  }, [orders, vectorizedDraftItemIds]);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      setHasLoadedVectorStatus(false);
      try {
        const [status, progressResponse] = await Promise.all([
          getEtsyDraftVectorStatus().catch(() => ({ total: 0, vectorized: 0, vectorizedDraftItemIds: [] })),
          getEtsyMassMatchProgress('').catch(() => ({ data: { jobId: 'initial', status: 'completed' as const, progress: 100, draftTotal: 0, draftProcessed: 0, uploadTotal: 0, uploadProcessed: 0, matchedCount: 0, failedCount: 0, productionVectorCount: 0, estimatedTimeSeconds: 0, results: [] } }))
        ]);
        
        console.log('[EtsyMassMatch] Initial Fetch results:', { status, progressResponse });

        if (cancelled) return;

        const readyIds = new Set(status.vectorizedDraftItemIds);
        setVectorizedDraftItemIds(readyIds);
        
        // Correctly unwrap progress from the response
        const initialProgress = 'data' in progressResponse ? (progressResponse.data as EtsyMassMatchProgress) : progressResponse;
        console.log('[EtsyMassMatch] Unwrapped Initial Progress:', initialProgress);
        
        if (initialProgress) {
          applyProgress(initialProgress);
        }

        setHasLoadedVectorStatus(true);
        setDraftSlots((previous) => previous.map((slot) => (
          slot.status === 'pending' && readyIds.has(slot.id)
            ? { ...slot, status: 'ready' }
            : slot
        )));
      } catch {
        if (!cancelled) {
          setVectorizedDraftItemIds(new Set());
          setHasLoadedVectorStatus(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [orders]);

  useEffect(() => () => {
    previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
  }, []);

  const applyProgress = useCallback((nextProgress: EtsyMassMatchProgress) => {
    // If the job is an 'initial' or static one, don't clear the progress state if we want to keep counts
    setProgress((prev) => {
      // Always preserve the highest known productionVectorCount
      const maxCount = Math.max(
        prev?.productionVectorCount ?? 0,
        nextProgress.productionVectorCount ?? 0
      );
      
      const mergedProgress = {
        ...nextProgress,
        productionVectorCount: maxCount > 0 ? maxCount : prev?.productionVectorCount
      };

      // Apply UI deduplication whenever progress is updated
      setUploadingFiles((prevFiles) => {
        const results = nextProgress.results ?? [];
        
        console.log('[EtsyMassMatch] Deduplication sync check:', {
          prevFilesCount: prevFiles.length,
          resultsCount: results.length,
          jobStatus: nextProgress.status
        });

        // 1. Map existing state to include new backend IDs from results
        const updated = prevFiles.map((file) => {
          // Find result by:
          // 1. Exact temporary ID (first upload session)
          // 2. Exact filename (for sub-sequent sessions or duplicates)
          const result = results.find(r => r.uploadedFileId === file.id || file.file.name === r.filename);
          
          if (!result) {
            // If the job is completed and we still don't have a result for this file
            if (
              activeUploadIdsRef.current.includes(file.id)
              && (nextProgress.status === 'completed' || nextProgress.status === 'failed')
            ) {
               return { ...file, status: 'ready' as const, progress: 100 };
            }
            return file;
          }

          const isMatched = result.status === 'matched' || result.matchedItemId != null;

          // Force the backend ID into the file state so we can filter by it
          return {
            ...file,
            status: (result.status === 'failed' ? 'failed' : isMatched ? 'matched' : 'ready') as UploadingFile['status'],
            progress: 100,
            matchedItemId: result.matchedItemId ?? undefined,
            uploadedFileId: result.uploadedFileId, // This is the unique hash from backend
            similarity: result.similarity,
          };
        });

        // 2. Perform strict deduplication by backend hash ONLY
        // We do NOT use name/size here because we want to see all selected files 
        // during upload and only merge them once the backend returns the hash.
        const seenBackendIds = new Set<string>();
        const finalFiltered = updated.filter(file => {
          // If we have a backend ID (hash), deduplicate by it
          if (file.uploadedFileId) {
            if (seenBackendIds.has(file.uploadedFileId)) {
              console.log('[EtsyMassMatch] FOUND DUPLICATE CONTENT (Backend ID):', file.uploadedFileId);
              return false;
            }
            seenBackendIds.add(file.uploadedFileId);
            return true;
          }
          
          // If we don't have a backend ID yet, we keep it in the queue
          return true;
        });

        console.log('[EtsyMassMatch] Final queue after dedupe:', finalFiltered.length);
        return finalFiltered;
      });

      return mergedProgress;
    });
    
    setDraftSlots((previous) => {
      const results = nextProgress.results ?? [];
      const byItemId = new Map(results.map((result) => [result.matchedItemId, result]));
      
      return previous.map((slot) => {
        const result = byItemId.get(slot.id);
        
        // Any result from the AI is a potential match, even if score is low
        const hasAiSuggestion = result != null && result.uploadedFileId != null;

        if (hasAiSuggestion) {
          // Find the preview URL from our local uploading files state if possible
          const matchedFile = uploadingFiles.find(f => 
            f.uploadedFileId === result.uploadedFileId || f.file.name === result.filename
          );
          let matchedUrl = slot.matchedFileUrl || matchedFile?.previewUrl;

          // If we still don't have a URL and we have a file ID, fetch it from backend
          if (!matchedUrl && result.uploadedFileId) {
             void (async () => {
               try {
                 const { signedUrl } = await getSignedUrlForFile(result.uploadedFileId!);
                 setDraftSlots(current => current.map(s => 
                   s.id === slot.id ? { ...s, matchedFileUrl: signedUrl } : s
                 ));
               } catch (err) {
                 console.error('Failed to fetch signed URL for auto-matched image:', err);
               }
             })();
          }

          return {
            ...slot,
            status: 'auto_matched',
            matchedFileId: result.uploadedFileId,
            matchedFileUrl: matchedUrl,
            similarity: result.similarity ?? undefined,
          };
        }

        return {
          ...slot,
          status: nextProgress.status === 'processing' && nextProgress.draftProcessed < nextProgress.draftTotal
            ? 'vectorizing'
            : 'ready',
        };
      });
    });
  }, [t]);

  useEffect(() => {
    if (!activeJobId) return undefined;

    let cancelled = false;
    const interval = window.setInterval(() => {
      void (async () => {
        try {
          const response = await getEtsyMassMatchProgress(activeJobId);
          // Safely unwrap data from response
          const nextProgress = 'data' in response ? (response.data as EtsyMassMatchProgress) : response;
          
          if (cancelled) return;
          applyProgress(nextProgress);

          if (nextProgress.status === 'completed' || nextProgress.status === 'failed') {
            window.clearInterval(interval);
            setActiveJobId(null);
            setIsVectorizingDrafts(false);

            // DEBUG: Log all results to console for investigation
            console.log('[EtsyMassMatch] Job Finished. Detailed Results:', {
              jobId: nextProgress.jobId,
              status: nextProgress.status,
              matchedCount: nextProgress.matchedCount,
              results: nextProgress.results?.map(r => ({
                itemTitle: draftSlots.find(s => s.id === r.matchedItemId)?.title,
                itemId: r.matchedItemId,
                score: r.similarity ? `${Math.round(r.similarity * 100)}%` : '0%',
                status: r.status,
                fileId: r.uploadedFileId
              }))
            });

            if (nextProgress.status === 'completed') {
              const hasMatches = nextProgress.matchedCount > 0;
              toast({
                title: hasMatches 
                  ? t('integrations.etsy.matching.massMatch.completed', 'Matching completed')
                  : t('integrations.etsy.matching.massMatch.noMatchesFound', 'No matches found'),
                description: hasMatches
                  ? t('integrations.etsy.matching.massMatch.completedDesc', 'Successfully matched {{matched}} items. {{failed}} failed.', {
                      matched: nextProgress.matchedCount,
                      failed: nextProgress.failedCount,
                    })
                  : t('integrations.etsy.matching.massMatch.noMatchesDesc', 'AI could not find high-confidence matches for the remaining slots.'),
              });
              onCompleted?.();
            }
          }
        } catch (error) {
          console.error('[EtsyMassMatch] Error polling progress:', error);
          window.clearInterval(interval);
          setActiveJobId(null);
          setIsVectorizingDrafts(false);
          activeUploadIdsRef.current = [];
        }
      })();
    }, 1500);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [activeJobId, applyProgress, onCompleted]);

  const startDraftVectorization = useCallback(async () => {
    console.log('[EtsyMassMatch] Starting vectorization. Current slots:', draftSlots.map(s => ({ id: s.id, status: s.status, title: s.title })));
    setIsVectorizingDrafts(true);

    try {
      const response = await startEtsyDraftVectorization();
      const nextProgress = 'data' in response ? (response.data as EtsyMassMatchProgress) : response;
      setActiveJobId(nextProgress.jobId);
      applyProgress(nextProgress);
    } catch {
      setIsVectorizingDrafts(false);
      toast({
        title: t('common.error', 'Error'),
        description: t('integrations.etsy.matching.massMatch.vectorizeFailed', 'Failed to start draft vectorization.'),
        variant: 'destructive',
      });
    }
  }, [applyProgress, t, toast]);

  useEffect(() => {
    if (!hasLoadedVectorStatus || isVectorizingDrafts || activeJobId || draftSlots.length === 0) {
      return;
    }

    const hasMissingVectors = draftSlots.some((slot) => (
      slot.status === 'pending' && !vectorizedDraftItemIds.has(slot.id)
    ));

    if (hasMissingVectors) {
      console.log('[EtsyMassMatch] Auto-starting vectorization for missing items');
      void startDraftVectorization();
    }
  }, [
    activeJobId,
    draftSlots,
    hasLoadedVectorStatus,
    isVectorizingDrafts,
    startDraftVectorization,
    vectorizedDraftItemIds,
  ]);

  const handleSelectedFiles = useCallback(async (files: File[]) => {
    if (files.length === 0) return;

    const newFiles = files.map(file => ({
      id: `${file.name}-${file.size}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      file,
      previewUrl: URL.createObjectURL(file),
      status: 'queued' as const,
      progress: 0,
    }));
    previewUrlsRef.current.push(...newFiles.map(file => file.previewUrl));

    setUploadingFiles((previous) => {
      // Create a map of existing files for quick lookup
      const existingFilesMap = new Map(previous.map(f => [f.id, f]));
      
      // Update or add new files
      newFiles.forEach(nf => {
        existingFilesMap.set(nf.id, nf);
      });

      return Array.from(existingFilesMap.values());
    });

    try {
      const uploadIds = newFiles.map((file) => file.id);

      activeUploadIdsRef.current = uploadIds;
      setUploadingFiles(prev => prev.map(file => (
        uploadIds.includes(file.id)
          ? { ...file, status: 'uploading' as const, progress: 5, error: undefined }
          : file
      )));

      const response = await uploadAndMatchEtsyProductionFiles(files);
      const nextProgress = 'data' in response ? (response.data as EtsyMassMatchProgress) : response;
      // Preserve productionVectorCount if the new progress doesn't have it yet
      const updatedProgress: EtsyMassMatchProgress = {
        ...nextProgress,
        productionVectorCount: nextProgress.productionVectorCount ?? progress?.productionVectorCount
      };

      applyProgress(updatedProgress);

      let currentProgress = updatedProgress;
      while (currentProgress.status !== 'completed' && currentProgress.status !== 'failed') {
        await sleep(1500);
        const progressResponse = await getEtsyMassMatchProgress(nextProgress.jobId);
        currentProgress = 'data' in progressResponse
          ? (progressResponse.data as EtsyMassMatchProgress)
          : progressResponse;
        applyProgress({
          ...currentProgress,
          productionVectorCount: currentProgress.productionVectorCount ?? progress?.productionVectorCount,
        });
      }

      if (currentProgress.status === 'failed') {
        throw new Error(currentProgress.error ?? t('integrations.etsy.matching.massMatch.uploadFailed', 'Upload failed'));
      }

      activeUploadIdsRef.current = [];
      toast({
        title: t('integrations.etsy.matching.massMatch.uploadStarted', 'Mass matching started'),
        description: t('integrations.etsy.matching.massMatch.uploadStartedDesc', 'Uploads are being matched against Etsy slots.'),
      });
    } catch {
      activeUploadIdsRef.current = [];
      setUploadingFiles(prev => prev.map(file => (
        newFiles.some(newFile => newFile.id === file.id) && (file.status === 'queued' || file.status === 'uploading')
          ? { ...file, status: 'failed', error: t('integrations.etsy.matching.massMatch.uploadFailed', 'Upload failed') }
          : file
      )));
      toast({
        title: t('common.error', 'Error'),
        description: t('integrations.etsy.matching.massMatch.uploadFailed', 'Upload failed'),
        variant: 'destructive',
      });
    }
  }, [applyProgress, progress?.productionVectorCount, t, toast]);

  const handleMatchExistingVectors = async () => {
    if (isBusy || activeJobId) return;
    try {
      activeUploadIdsRef.current = [];
      const response = await matchExistingEtsyProductionVectors();
      const nextProgress = (response as { data?: EtsyMassMatchProgress }).data || (response as EtsyMassMatchProgress);
      setActiveJobId(nextProgress.jobId);
      
      // Preserve productionVectorCount if the new progress doesn't have it yet
      const updatedProgress = {
        ...nextProgress,
        productionVectorCount: nextProgress.productionVectorCount ?? progress?.productionVectorCount
      };
      
      applyProgress(updatedProgress);
      toast({
        title: t('integrations.etsy.matching.massMatch.existingStarted', 'AI matching started'),
        description: t('integrations.etsy.matching.massMatch.existingStartedDesc', 'Matching Etsy slots with previously uploaded images.'),
      });
    } catch {
      toast({
        title: t('common.error', 'Error'),
        description: t('integrations.etsy.matching.massMatch.existingFailed', 'Failed to start AI matching.'),
        variant: 'destructive',
      });
    }
  };

  const completedUploads = uploadingFiles.filter(file => file.status === 'ready' || file.status === 'matched').length;
  const failedUploads = uploadingFiles.filter(file => file.status === 'failed').length;
  const activeUploads = uploadingFiles.filter(file => (
    file.status === 'queued' || file.status === 'uploading' || file.status === 'vectorizing' || file.status === 'matching'
  ));
  const isJobActive = (activeJobId != null || progress?.status === 'processing' || progress?.status === 'queued') && progress?.status !== 'completed' && progress?.status !== 'failed' && progress?.jobId !== 'initial';
  const isBusy = isVectorizingDrafts || isJobActive || activeUploads.length > 0;
  const fallbackProgress = activeUploads.length > 0
    ? Math.max(...activeUploads.map(file => file.progress), 5)
    : null;
  const visibleProgress = progress
    ? {
        progress: progress.progress,
        estimatedTimeSeconds: progress.estimatedTimeSeconds,
      }
    : fallbackProgress !== null
      ? {
          progress: fallbackProgress,
          estimatedTimeSeconds: null,
      }
      : null;

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleSelectedFiles,
    accept: { 'image/*': ['.jpeg', '.png', '.jpg', '.gif', '.tif', '.tiff', '.psd'] },
    multiple: true,
    disabled: isBusy,
  });

  const autoMatchedSlots = useMemo(() => draftSlots.filter(slot => slot.status === 'auto_matched'), [draftSlots]);

  const dbVectorCount = progress?.productionVectorCount ?? 0;
  const [isConfirmingAll, setIsConfirmingAll] = useState(false);

  const handleConfirmMatch = async (slotId: string) => {
    const slot = draftSlots.find(s => s.id === slotId);
    if (!slot || !slot.matchedFileId) return;

    try {
      await assignImportedItemFullSizeImage({
        userId: '', // Service uses current user
        importedItemId: slotId,
        uploadedFileId: slot.matchedFileId,
      });

      // Update status but DO NOT remove from list yet
      setDraftSlots(prev => prev.map(s => 
        s.id === slotId ? { ...s, status: 'matched' as const } : s
      ));

      toast({
        title: t('integrations.etsy.matching.massMatch.confirmed', 'Match confirmed'),
        description: t('integrations.etsy.matching.massMatch.confirmedDesc', 'Image assignment finalized.'),
      });
    } catch {
      toast({
        title: t('common.error', 'Error'),
        description: t('integrations.etsy.matching.massMatch.confirmFailed', 'Failed to confirm match.'),
        variant: 'destructive',
      });
    }
  };

  const handleConfirmAllMatches = async () => {
    if (autoMatchedSlots.length === 0) return;
    
    setIsConfirmingAll(true);
    const successfulIds: string[] = [];
    let failCount = 0;

    try {
      // Process in small batches or sequence to avoid overwhelming the backend
      for (const slot of autoMatchedSlots) {
        if (!slot.matchedFileId) continue;
        
        try {
          await assignImportedItemFullSizeImage({
            userId: '',
            importedItemId: slot.id,
            uploadedFileId: slot.matchedFileId,
          });
          successfulIds.push(slot.id);
        } catch (err) {
          console.error(`Failed to confirm slot ${slot.id}:`, err);
          failCount++;
        }
      }

      // Update status for all successfully confirmed slots
      const confirmedIdsSet = new Set(successfulIds);
      setDraftSlots(prev => prev.map(s => 
        confirmedIdsSet.has(s.id) ? { ...s, status: 'matched' as const } : s
      ));

      toast({
        title: t('integrations.etsy.matching.massMatch.bulkConfirmed', 'Bulk confirmation finished'),
        description: t('integrations.etsy.matching.massMatch.bulkConfirmedDesc', 'Confirmed {{success}} matches. {{failed}} failed.', {
          success: successfulIds.length,
          failed: failCount
        }),
      });
      
      if (successfulIds.length > 0) {
        onCompleted?.();
      }
    } finally {
      setIsConfirmingAll(false);
    }
  };

  // The processing queue should show if we have active uploads OR if we have vectors in the DB to show the Vault
  const showProcessingQueue = uploadingFiles.length > 0 || dbVectorCount > 0;

  console.log('[EtsyMassMatch] Render Debug:', {
    dbVectorCount,
    uploadingFilesCount: uploadingFiles.length,
    hasLoadedVectorStatus,
    showProcessingQueue,
    progressJobId: progress?.jobId,
    progressStatus: progress?.status
  });

  return (
    <div className="grid gap-6 xl:grid-cols-[350px_1fr]">
      <Card className="flex flex-col h-[calc(100vh-25rem)]">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center justify-between">
            <div className="flex items-center gap-2">
              {t('integrations.etsy.matching.massMatch.unmatchedDrafts', 'Unmatched Drafts')}
              <Badge variant="secondary">{draftSlots.length}</Badge>
            </div>
            {autoMatchedSlots.length > 0 && (
              <Button 
                size="sm" 
                variant="default" 
                className="h-7 px-2 text-[10px] gap-1 shadow-sm"
                onClick={handleConfirmAllMatches}
                disabled={isConfirmingAll}
              >
                {isConfirmingAll ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                {t('integrations.etsy.matching.massMatch.confirmAll', 'Confirm {{count}}', { count: autoMatchedSlots.length })}
              </Button>
            )}
          </CardTitle>
          <CardDescription className="text-xs">
            {t('integrations.etsy.matching.massMatch.draftsDesc', 'Slots to be filled by uploaded files.')}
          </CardDescription>
        </CardHeader>
        <ScrollArea className="flex-1 px-4">
          <div className="space-y-3 pb-4">
            {draftSlots.map((slot) => (
              (() => {
                const originalPrintSizeLabel = formatPrintSizeLabel(slot.originalPrintWidthIn, slot.originalPrintHeightIn);
                const expectedPrintSizeLabel = formatPrintSizeLabel(slot.expectedWidthIn, slot.expectedHeightIn);

                return (
                  <div 
                    key={slot.id} 
                    className={`group relative flex items-center gap-3 rounded-lg border p-3 transition-all ${
                      slot.status === 'matched' ? 'border-emerald-200 bg-emerald-50/30' : 'border-dashed border-border bg-muted/10'
                    }`}
                  >
                    <div className="flex gap-2 shrink-0">
                      <div className="flex w-16 flex-col items-center gap-1">
                        <div className="h-14 w-14 rounded-md border border-border bg-background flex items-center justify-center overflow-hidden shadow-sm">
                          {slot.thumbnailUrl ? (
                            <img src={slot.thumbnailUrl} alt={slot.title} className="h-full w-full object-cover" />
                          ) : (
                            <ImageIcon className="h-5 w-5 text-muted-foreground/40" />
                          )}
                        </div>
                        <span className="text-[8px] text-muted-foreground uppercase font-semibold">{t('integrations.etsy.matching.massMatch.etsyLabel', 'Etsy')}</span>
                        {expectedPrintSizeLabel && (
                          <span className="text-center text-[8px] font-semibold leading-tight text-blue-700">
                            {t('integrations.etsy.matching.massMatch.expectedSize', 'Expected')}<br />
                            {expectedPrintSizeLabel}
                          </span>
                        )}
                      </div>
                      
                      {(slot.status === 'matched' || slot.status === 'auto_matched') ? (
                        <div className="flex w-16 flex-col items-center gap-1 animate-in fade-in slide-in-from-left-2 duration-300">
                          <div className="h-14 w-14 rounded-md border border-emerald-200 bg-background flex items-center justify-center overflow-hidden shadow-sm relative">
                            {slot.matchedFileUrl ? (
                              <img src={slot.matchedFileUrl} alt={t('integrations.etsy.matching.massMatch.matchedProductionFileAlt', 'Matched production file')} className="h-full w-full object-cover" />
                            ) : (
                              <div className="h-full w-full bg-emerald-50 flex items-center justify-center">
                                <Check className="h-5 w-5 text-emerald-400" />
                              </div>
                            )}
                            {slot.status === 'matched' && (
                              <div className="absolute inset-0 bg-emerald-500/20 flex items-center justify-center">
                                <Check className="h-6 w-6 text-emerald-600 drop-shadow-sm" />
                              </div>
                            )}
                          </div>
                          <span className="text-[8px] text-emerald-600 uppercase font-semibold">{t('integrations.etsy.matching.massMatch.matchLabel', 'Match')}</span>
                          {originalPrintSizeLabel && (
                            <span className="text-center text-[8px] font-semibold leading-tight text-emerald-700">
                              {t('integrations.etsy.matching.massMatch.realSizeAt300Dpi', 'Real at 300 PPI')}<br />
                              {originalPrintSizeLabel}
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className="flex w-16 flex-col items-center gap-1">
                          <div className="h-14 w-14 rounded-md border border-dashed border-border bg-muted/5 flex items-center justify-center overflow-hidden">
                            <ImageIcon className="h-4 w-4 text-muted-foreground/20" />
                          </div>
                          <span className="text-[8px] text-muted-foreground/30 uppercase font-semibold">{t('integrations.etsy.matching.massMatch.matchLabel', 'Match')}</span>
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1 space-y-2">
                      <div className="text-xs font-semibold text-foreground leading-snug">{slot.title}</div>
                  
                  {slot.status === 'auto_matched' && (
                    <div className="space-y-2">
                      <Button
                        size="sm"
                        variant="default"
                        className="w-full h-8 text-xs gap-2 font-bold shadow-sm"
                        onClick={() => handleConfirmMatch(slot.id)}
                      >
                        <Check className="h-3.5 w-3.5 stroke-[3]" />
                        {t('integrations.etsy.matching.massMatch.confirmBtn', 'Confirm match')}
                      </Button>
                    </div>
                  )}

                  {slot.status === 'matched' && (
                    <div className="flex items-center gap-2 text-xs text-emerald-700 font-bold bg-emerald-100/50 py-1.5 px-3 rounded-md border border-emerald-200 w-fit">
                      <Check className="h-4 w-4" />
                      {t('integrations.etsy.matching.massMatch.confirmedLabel', 'MATCHED')}
                    </div>
                  )}

                  <div className="flex items-center gap-3">
                    {slot.status === 'pending' && (
                      <span className="text-[10px] font-medium text-muted-foreground px-2 py-0.5 bg-muted rounded-full">
                        {t('integrations.etsy.matching.massMatch.waiting', 'Waiting...')}
                      </span>
                    )}
                    {slot.status === 'vectorizing' && (
                      <span className="flex items-center text-[10px] font-medium text-amber-700 px-2 py-0.5 bg-amber-50 rounded-full border border-amber-100">
                        <Loader2 className="mr-1.5 h-3 w-3 animate-spin" /> 
                        {t('integrations.etsy.matching.massMatch.vectorizing', 'Vectorizing...')}
                      </span>
                    )}
                    {slot.status === 'ready' && (
                      <span className="text-[10px] font-medium text-blue-700 px-2 py-0.5 bg-blue-50 rounded-full border border-blue-100">
                        {t('integrations.etsy.matching.massMatch.ready', 'Ready for match')}
                      </span>
                    )}
                    {slot.status === 'auto_matched' && (
                      <div className="flex items-center gap-1.5 text-[10px] font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full border border-blue-100 shadow-sm">
                        <Sparkles className="h-3 w-3" />
                        {t('integrations.etsy.matching.massMatch.aiSuggestion', 'AI Suggestion')} ({Math.round((slot.similarity || 0) * 100)}%)
                      </div>
                    )}
                  </div>
                </div>
              </div>
                );
              })()
            ))}
          </div>
        </ScrollArea>
      </Card>

      <div className="flex flex-col gap-6">
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <CardTitle className="text-lg">{t('integrations.etsy.matching.massMatch.uploadTitle', 'Mass Upload Production Files')}</CardTitle>
                <CardDescription>
                  {t('integrations.etsy.matching.massMatch.uploadDesc', 'Upload multiple full-size images. AI will automatically match them to the draft slots on the left. To match Etsy drafts with previously uploaded images, use the "Use AI for matching" button.')}
                </CardDescription>
              </div>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className="gap-2"
                disabled={isBusy || draftSlots.length === 0}
                onClick={() => void handleMatchExistingVectors()}
              >
                {isBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                {t('integrations.etsy.matching.massMatch.matchExisting', 'Use AI for matching')}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div 
              {...getRootProps()}
              className={`rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
                isDragActive ? 'border-primary bg-primary/10' : 'border-muted-foreground/50 hover:border-primary'
              } ${isBusy ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
            >
              <input {...getInputProps()} />
              <UploadCloud className="mx-auto h-12 w-12 text-muted-foreground" />
              <p className="mt-2 text-sm text-muted-foreground">
                {isDragActive
                  ? t('uploader.drop_active', 'Drop the files here...')
                  : t('uploader.drop_inactive', 'Drag and drop files here, or click to select files')}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {t('integrations.etsy.matching.massMatch.dropHelp', 'Supports JPG, PNG, TIFF. Large files upload directly.')}
              </p>
            </div>

            {visibleProgress && isJobActive && (
              <div className="mb-6 rounded-lg border bg-background p-4 shadow-sm">
                <div className="mb-2 flex items-center justify-between text-xs font-medium">
                  <span className="text-muted-foreground">
                    {t('integrations.etsy.matching.massMatch.progress', 'Progress')}: {visibleProgress.progress}%
                  </span>
                  {visibleProgress.estimatedTimeSeconds !== null && visibleProgress.estimatedTimeSeconds > 0 && (
                    <span className="text-muted-foreground">
                      {t('integrations.etsy.matching.massMatch.eta', 'ETA')}: {visibleProgress.estimatedTimeSeconds}s
                    </span>
                  )}
                </div>
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-background">
                  <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${visibleProgress.progress}%` }} />
                </div>
              </div>
            )}

          {showProcessingQueue && (
            <div className="mt-8 space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium">{t('integrations.etsy.matching.massMatch.processing', 'Processing queue')}</h4>
                <div className="text-xs text-muted-foreground flex items-center gap-3">
                  {uploadingFiles.length > 0 && (
                    <>
                      <span>{completedUploads} / {uploadingFiles.length} {t('integrations.etsy.matching.massMatch.done', 'done')}</span>
                      {failedUploads > 0 && <span>{failedUploads} {t('integrations.etsy.matching.massMatch.failed', 'failed')}</span>}
                    </>
                  )}
                </div>
              </div>
              <div className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                {dbVectorCount > 0 && (
                  <div className="relative group rounded-lg border-2 border-dashed border-primary/30 bg-primary/5 p-2 overflow-hidden aspect-square flex flex-col items-center justify-center text-center">
                    <Sparkles className="h-8 w-8 text-primary mb-2 animate-pulse" />
                    <div className="text-[10px] font-semibold text-primary uppercase tracking-wider">{t('integrations.etsy.matching.massMatch.database', 'AI Vault')}</div>
                    <div className="mt-1 text-xl font-bold text-primary">{dbVectorCount}</div>
                    <div className="text-[9px] text-primary/70 leading-tight px-1">
                      {t('integrations.etsy.matching.massMatch.availableVectors', 'previously uploaded images available')}
                    </div>
                  </div>
                )}
                {uploadingFiles.map((file) => (
                    <div key={file.id} className="relative group rounded-lg border bg-background p-2 overflow-hidden aspect-square">
                      <img src={file.previewUrl} className="h-full w-full object-cover rounded shadow-sm opacity-80" />
                      <div className="absolute inset-0 flex flex-col items-center justify-center p-2 bg-black/5">
                        {file.status === 'queued' && <div className="text-[10px] font-medium text-white drop-shadow-md">{t('integrations.etsy.matching.massMatch.queued', 'Queued...')}</div>}
                        {file.status === 'uploading' && <div className="text-[10px] font-medium text-white drop-shadow-md">{t('integrations.etsy.matching.massMatch.uploading', 'Uploading...')}</div>}
                        {file.status === 'vectorizing' && (
                          <div className="flex flex-col items-center text-white drop-shadow-md">
                            <Loader2 className="h-5 w-5 animate-spin mb-1" />
                            <span className="text-[10px] font-medium">{t('integrations.etsy.matching.massMatch.vectorizing', 'Vectorizing...')}</span>
                          </div>
                        )}
                        {file.status === 'matching' && (
                           <div className="flex flex-col items-center text-white drop-shadow-md">
                             <ArrowRight className="h-5 w-5 mb-1" />
                             <span className="text-[10px] font-medium">{t('integrations.etsy.matching.massMatch.matching', 'Matching...')}</span>
                           </div>
                        )}
                        {file.status === 'ready' && (
                          <div className="flex flex-col items-center gap-1 text-white drop-shadow-md">
                            <div className="bg-blue-500 rounded-full p-2 shadow-lg">
                              <ImageIcon className="h-6 w-6 text-white" />
                            </div>
                            <span className="rounded bg-black/50 px-1.5 py-0.5 text-[10px] font-medium">
                              {t('integrations.etsy.matching.massMatch.ready', 'Ready for match')}
                            </span>
                          </div>
                        )}
                        {file.status === 'matched' && (
                          <div className="flex flex-col items-center gap-1">
                            <div className="bg-emerald-500 rounded-full p-2 shadow-lg">
                              <Check className="h-6 w-6 text-white" />
                            </div>
                            <span className="rounded bg-black/50 px-1.5 py-0.5 text-[10px] font-medium text-white">
                              {t('integrations.etsy.matching.massMatch.matched', 'Matched')}
                            </span>
                            {file.similarity != null && (
                              <span className="rounded bg-black/50 px-1.5 py-0.5 text-[10px] font-medium text-white">
                                {Math.round(file.similarity * 100)}%
                              </span>
                            )}
                          </div>
                        )}
                        {file.status === 'failed' && (
                          <div className="flex max-w-full flex-col items-center px-2 text-center text-white drop-shadow-md">
                            <AlertCircle className="h-5 w-5 mb-1" />
                            <span className="max-w-full rounded bg-black/50 px-1.5 py-0.5 text-[10px] font-medium leading-tight line-clamp-2">
                              {file.error ?? t('integrations.etsy.matching.massMatch.failed', 'failed')}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
